import { net } from './net.js';
import { normalizeHost } from './profiles.js';

/* Cliente de la API Xtream Codes (player_api.php).
   Documentación de facto: acciones get_live_categories, get_live_streams, get_vod_categories,
   get_vod_streams, get_series_categories, get_series, get_vod_info, get_series_info,
   get_short_epg y el XMLTV completo en xmltv.php. */

export class XtreamClient {
    constructor(profile) {
        this.host = normalizeHost(profile.host);
        this.username = profile.username || '';
        this.password = profile.password || '';
    }

    get base() {
        return this.host + '/player_api.php?username=' + encodeURIComponent(this.username) + '&password=' + encodeURIComponent(this.password);
    }

    apiUrl(action, params) {
        let url = this.base;
        if (action) url += '&action=' + encodeURIComponent(action);
        if (params) {
            Object.keys(params).forEach((k) => {
                url += '&' + encodeURIComponent(k) + '=' + encodeURIComponent(params[k]);
            });
        }
        return url;
    }

    api(action, params, opts) {
        return net.fetchJson(this.apiUrl(action, params), opts);
    }

    /** Devuelve {user_info, server_info}; lanza error 'auth' si las credenciales no son válidas. */
    async auth() {
        const data = await this.api(null, null, { timeout: 15000 });
        if (!data || !data.user_info || String(data.user_info.auth) === '0' || data.user_info.status === 'Disabled') {
            const err = new Error('auth');
            err.code = 'auth';
            throw err;
        }
        return data;
    }

    liveCategories() { return this.api('get_live_categories'); }
    liveStreams() { return this.api('get_live_streams', null, { timeout: 60000 }); }
    vodCategories() { return this.api('get_vod_categories'); }
    vodStreams() { return this.api('get_vod_streams', null, { timeout: 90000 }); }
    seriesCategories() { return this.api('get_series_categories'); }
    series() { return this.api('get_series', null, { timeout: 90000 }); }
    vodInfo(id) { return this.api('get_vod_info', { vod_id: id }); }
    seriesInfo(id) { return this.api('get_series_info', { series_id: id }); }
    shortEpg(streamId, limit = 10) { return this.api('get_short_epg', { stream_id: streamId, limit }); }

    xmltvUrl() {
        return this.host + '/xmltv.php?username=' + encodeURIComponent(this.username) + '&password=' + encodeURIComponent(this.password);
    }

    liveUrl(streamId, format = 'm3u8') {
        const ext = format === 'ts' ? 'ts' : 'm3u8';
        return this.host + '/live/' + encodeURIComponent(this.username) + '/' + encodeURIComponent(this.password) + '/' + streamId + '.' + ext;
    }

    vodUrl(streamId, ext) {
        return this.host + '/movie/' + encodeURIComponent(this.username) + '/' + encodeURIComponent(this.password) + '/' + streamId + '.' + (ext || 'mp4');
    }

    seriesUrl(episodeId, ext) {
        return this.host + '/series/' + encodeURIComponent(this.username) + '/' + encodeURIComponent(this.password) + '/' + episodeId + '.' + (ext || 'mp4');
    }

    /** Catch-up (timeshift): start = Date, durationMinutes = número. */
    timeshiftUrl(streamId, start, durationMinutes) {
        const d = start instanceof Date ? start : new Date(start);
        const pad = (n) => (n < 10 ? '0' + n : '' + n);
        const stamp = d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()) + ':' + pad(d.getHours()) + '-' + pad(d.getMinutes());
        return this.host + '/streaming/timeshift.php?username=' + encodeURIComponent(this.username) +
            '&password=' + encodeURIComponent(this.password) +
            '&stream=' + streamId + '&start=' + stamp + '&duration=' + Math.max(1, Math.round(durationMinutes));
    }
}

/* Normalización de las respuestas del panel a los modelos de la app. */

export function normalizeLive(raw, index) {
    return {
        id: String(raw.stream_id),
        num: Number(raw.num) || index + 1,
        name: raw.name || ('Canal ' + raw.stream_id),
        logo: raw.stream_icon || '',
        categoryId: raw.category_id != null ? String(raw.category_id) : '',
        epgId: (raw.epg_channel_id || '').trim().toLowerCase(),
        archive: String(raw.tv_archive) === '1',
        archiveDays: Number(raw.tv_archive_duration) || 0,
        added: Number(raw.added) || 0
    };
}

export function normalizeVod(raw) {
    return {
        id: String(raw.stream_id),
        name: raw.name || raw.title || '',
        poster: raw.stream_icon || raw.cover || '',
        categoryId: raw.category_id != null ? String(raw.category_id) : '',
        ext: raw.container_extension || 'mp4',
        added: Number(raw.added) || 0,
        rating: Number(raw.rating) || Number(raw.rating_5based) * 2 || 0,
        year: extractYear(raw.year || raw.releaseDate || raw.release_date || raw.name),
        plot: raw.plot || '',
        genre: raw.genre || '',
        duration: raw.duration || '',
        tmdb: raw.tmdb || raw.tmdb_id || '',
        imdb: raw.imdb || raw.imdb_id || ''
    };
}

export function normalizeSeries(raw) {
    return {
        id: String(raw.series_id),
        name: raw.name || raw.title || '',
        poster: raw.cover || raw.stream_icon || '',
        backdrop: Array.isArray(raw.backdrop_path) ? raw.backdrop_path[0] || '' : raw.backdrop_path || '',
        categoryId: raw.category_id != null ? String(raw.category_id) : '',
        plot: raw.plot || '',
        cast: raw.cast || '',
        director: raw.director || '',
        genre: raw.genre || '',
        releaseDate: raw.releaseDate || raw.release_date || '',
        year: extractYear(raw.releaseDate || raw.release_date || raw.year || raw.name),
        rating: Number(raw.rating) || Number(raw.rating_5based) * 2 || 0,
        added: Number(raw.last_modified) || 0,
        youtube: raw.youtube_trailer || '',
        tmdb: raw.tmdb || raw.tmdb_id || ''
    };
}

export function normalizeCategory(raw) {
    return {
        id: String(raw.category_id),
        name: raw.category_name || String(raw.category_id),
        parent: raw.parent_id != null ? String(raw.parent_id) : '0'
    };
}

export function normalizeVodInfo(data, base) {
    const info = (data && data.info) || {};
    const movie = (data && data.movie_data) || {};
    return Object.assign({}, base, {
        plot: info.plot || info.description || base.plot || '',
        cast: info.cast || info.actors || info.stars || (movie && movie.cast) || '',
        director: info.director || '',
        genre: info.genre || base.genre || '',
        duration: info.duration || base.duration || '',
        durationSecs: Number(info.duration_secs) || 0,
        age: info.age || info.mpaa_rating || '',
        backdrop: Array.isArray(info.backdrop_path) ? info.backdrop_path[0] || '' : info.backdrop_path || '',
        poster: info.movie_image || info.cover_big || base.poster || '',
        country: info.country || '',
        releaseDate: info.releasedate || info.release_date || '',
        year: extractYear(info.releasedate || info.release_date) || base.year,
        rating: Number(info.rating) || Number(info.tmdb_rating) || base.rating || 0,
        youtube: info.youtube_trailer || '',
        ext: movie.container_extension || base.ext,
        audio: info.audio || null,
        video: info.video || null,
        bitrate: info.bitrate || 0,
        tmdb: info.tmdb_id || info.tmdb || base.tmdb || '',
        imdb: info.imdb_id || info.imdb || base.imdb || ''
    });
}

export function normalizeSeriesInfo(data, base) {
    const info = (data && data.info) || {};
    const episodesRaw = (data && data.episodes) || {};
    const seasonsMeta = {};
    ((data && data.seasons) || []).forEach((s) => {
        seasonsMeta[String(s.season_number)] = s;
    });

    const seasons = Object.keys(episodesRaw)
        .map((num) => {
            const meta = seasonsMeta[num] || {};
            const list = Array.isArray(episodesRaw[num]) ? episodesRaw[num] : Object.values(episodesRaw[num]);
            return {
                number: Number(num),
                name: meta.name || ('Temporada ' + num),
                poster: meta.cover || meta.cover_big || '',
                overview: meta.overview || '',
                episodes: list.map((ep) => {
                    const epInfo = ep.info || {};
                    return {
                        id: String(ep.id),
                        seriesId: base.id,
                        season: Number(ep.season || num),
                        num: Number(ep.episode_num) || 0,
                        title: ep.title || ('Episodio ' + ep.episode_num),
                        ext: ep.container_extension || 'mp4',
                        plot: epInfo.plot || '',
                        durationSecs: Number(epInfo.duration_secs) || 0,
                        duration: epInfo.duration || '',
                        thumb: epInfo.movie_image || epInfo.cover_big || '',
                        rating: Number(epInfo.rating) || 0,
                        airDate: epInfo.releasedate || epInfo.air_date || '',
                        audio: epInfo.audio || null,
                        video: epInfo.video || null,
                        bitrate: epInfo.bitrate || 0
                    };
                }).sort((a, b) => a.num - b.num)
            };
        })
        .filter((s) => s.episodes.length)
        .sort((a, b) => a.number - b.number);

    return Object.assign({}, base, {
        plot: info.plot || base.plot || '',
        cast: info.cast || info.actors || info.stars || base.cast || '',
        director: info.director || base.director || '',
        genre: info.genre || base.genre || '',
        releaseDate: info.releaseDate || info.release_date || base.releaseDate || '',
        year: extractYear(info.releaseDate || info.release_date) || base.year,
        rating: Number(info.rating) || base.rating || 0,
        backdrop: Array.isArray(info.backdrop_path) ? info.backdrop_path[0] || base.backdrop : info.backdrop_path || base.backdrop,
        poster: info.cover || base.poster,
        youtube: info.youtube_trailer || base.youtube || '',
        age: info.age || '',
        episodeRunTime: info.episode_run_time || '',
        tmdb: info.tmdb || info.tmdb_id || base.tmdb || '',
        imdb: info.imdb_id || info.imdb || base.imdb || '',
        seasons
    });
}

export function extractYear(value) {
    if (!value) return '';
    const m = String(value).match(/(19|20)\d{2}/);
    return m ? m[0] : '';
}
